import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdDelete, MdVisibility, MdClose, MdSearch, MdCheckCircle, MdInbox } from 'react-icons/md';
import ImageCropper from '../components/ImageCropper';
import WebPreviewModal from '../components/WebPreviewModal';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import { formatDate, isAllIndiaState, isInternationalState, statesMatch, ALL_INDIA_STATE, INTERNATIONAL_STATE, displayState } from '../utils/format';

function SearchableSelect({ label, value, options, onChange, placeholder = "Search...", searchKey = "slug" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter(opt =>
    (opt.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (opt[searchKey] || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="form-group">
      <label>{label}</label>
      <div className="searchable-select">
        <div className="select-box" onClick={() => setIsOpen(!isOpen)}>
          {(typeof value === 'string' ? displayState(value) : value) || `Select ${label}`}
        </div>
        {isOpen && (
          <div className="select-dropdown">
            <input
              autoFocus
              placeholder={placeholder}
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
            />
            <div className="options-list">
              {filtered.map(opt => (
                <div
                  key={opt.id || opt.name}
                  className="option-item"
                  onClick={() => {
                    onChange(opt.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span>{displayState(opt.name)}</span>
                  {opt[searchKey] && <span className="text-muted text-xs">({opt[searchKey]})</span>}
                </div>
              ))}
              {filtered.length === 0 && <div className="option-item text-muted">No results found</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ReviewArticles() {
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [filters, setFilters] = useState({ category: '', city: '', admin_id: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const { page, limit, setPage, setLimit } = usePagination(15);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const stateMapping = {
    'ఆంధ్రప్రదేశ్': 'Andhra Pradesh',
    'తెలంగాణ': 'Telangana',
    'జాతీయం': 'All India',
    // Legacy "ఆల్/అల్ ఇండియా" spellings — tolerated for old rows, not written going forward.
    'ఆల్ ఇండియా': 'All India',
    'ఆల్ ఇండియా ': 'All India',
    'అల్ ఇండియా': 'All India',
    'అల్ ఇండియా ': 'All India',
    'ఆంధ్ర ప్రదేశ్': 'Andhra Pradesh',
    'Telangana': 'Telangana',
    'Andhra Pradesh': 'Andhra Pradesh'
  };
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Queue navigation: reviewed one article at a time (like Contributor
  // Review) instead of a table + separate "view" modal. `current` is
  // derived from `articles[currentIndex]` rather than kept as its own
  // state, so approving/rejecting/editing and re-fetching the list can
  // never leave a stale copy behind.
  const [currentIndex, setCurrentIndex] = useState(0);
  // 'start' | 'end' | null — which edge of a newly-loaded page (after
  // Next/Previous crosses a page boundary) the queue should land on.
  const [pendingEdge, setPendingEdge] = useState(null);
  const [showQueueList, setShowQueueList] = useState(false);
  const [showWebPreview, setShowWebPreview] = useState(false);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [approving, setApproving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [viewMatchedArticle, setViewMatchedArticle] = useState(null);

  // Schedule modal
  const [scheduleModal, setScheduleModal] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduling, setScheduling] = useState(false);

  // Approve is one click — no confirmation step. (Duplicate-check feature
  // disabled for now — commented out, not removed.)
  const handlePreApprove = async (article) => {
    // setCheckingDuplicate(true);
    // try {
    //   const res = await api.post('/admin/articles/check-duplicate', {
    //     title: article.title,
    //     summary: article.summary,
    //     category: article.category
    //   });
    //   if (res.data.success && res.data.has_duplicates) {
    //     setDuplicateWarning({ article, duplicates: res.data.duplicates });
    //     return;
    //   }
    // } catch (err) {
    //   // fall through to approve
    // } finally {
    //   setCheckingDuplicate(false);
    // }
    setApproving(true);
    try {
      await api.put(`/admin/articles/${article.id}`, { status: 'published' });
      toast.success('Article Approved & Published');
      fetchArticles();
    } catch (err) {
      toast.error('Approve failed');
    } finally {
      setApproving(false);
    }
  };


  const fetchArticles = useCallback(async () => {
    setLoading(true);
    window.scrollTo(0, 0);
    try {
      const params = { page, limit, status: 'draft' };
      if (filters.category) params.category = filters.category;
      if (filters.city) params.city = filters.city;
      if (filters.admin_id) params.admin_id = filters.admin_id;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const res = await api.get('/admin/articles', { params });
      setArticles(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [filters, searchQuery, page, limit]);

  // Fetch instantly on page/limit change (page clicks shouldn't feel debounced).
  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  // Reset to page 1 (debounced) when search/filters actually change — but not
  // on first mount, so a bookmarked/refreshed ?page=3 URL sticks. If already
  // on page 1, setPage(1) wouldn't change anything, so fetch directly.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    const delayDebounceFn = setTimeout(() => {
      if (page === 1) fetchArticles(); else setPage(1);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filters]);

  useEffect(() => {
    api.get('/categories').then(res => setCategories(res.data.data)).catch(() => { });
    api.get('/cities').then(res => setCities(res.data.data)).catch(() => { });
    api.get('/admin/admins').then(res => setUsers(res.data.data)).catch(() => { });
  }, []);

  const filteredArticles = articles;

  const current = articles[currentIndex] || null;

  // Inline title/summary editing directly on the review card — no modal.
  // Drafts re-sync whenever the queue moves to a different article; a save
  // fires on blur (or Enter for the single-line title) and only sends the
  // one changed field, same PUT-and-merge pattern as toggleViewFlag below.
  const [titleDraft, setTitleDraft] = useState('');
  const [summaryDraft, setSummaryDraft] = useState('');
  const [savingField, setSavingField] = useState(null); // 'title' | 'summary' | null
  const summaryRef = useRef(null);

  useEffect(() => {
    setTitleDraft(current?.title || '');
    setSummaryDraft(current?.summary || '');
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (summaryRef.current) {
      summaryRef.current.style.height = 'auto';
      summaryRef.current.style.height = `${summaryRef.current.scrollHeight}px`;
    }
  }, [summaryDraft]);

  const saveInlineField = async (field, value) => {
    if (!current || value === (current[field] || '')) return;
    setSavingField(field);
    try {
      await api.put(`/admin/articles/${current.id}`, { [field]: value });
      setArticles(list => list.map(a => a.id === current.id ? { ...a, [field]: value } : a));
      toast.success('Saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
      if (field === 'title') setTitleDraft(current.title || '');
      else setSummaryDraft(current.summary || '');
    } finally {
      setSavingField(null);
    }
  };

  // Switching filters/search starts a different queue — always land on its first item.
  useEffect(() => {
    setCurrentIndex(0);
  }, [filters, searchQuery]);

  // Runs whenever a fresh page of results lands (page change, or a refetch
  // after approve/reject/schedule). If Next/Previous just crossed a page
  // boundary, land on the edge being navigated toward; otherwise keep the
  // current position (so approving item #3 naturally reveals whatever is
  // now #3 — the next one in the queue) and only clamp if that position no
  // longer exists (e.g. the last item on the page was just handled).
  useEffect(() => {
    if (pendingEdge === 'end') {
      setCurrentIndex(Math.max(0, articles.length - 1));
      setPendingEdge(null);
    } else if (pendingEdge === 'start') {
      setCurrentIndex(0);
      setPendingEdge(null);
    } else if (currentIndex >= articles.length) {
      setCurrentIndex(Math.max(0, articles.length - 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  const isFirstOverall = currentIndex <= 0 && pagination.page <= 1;
  const isLastOverall = currentIndex >= articles.length - 1 && pagination.page >= pagination.pages;

  const goPrev = () => {
    if (!articles.length) return;
    if (currentIndex > 0) {
      setCurrentIndex((i) => i - 1);
    } else if (pagination.page > 1) {
      setPendingEdge('end');
      setPage(pagination.page - 1);
    }
  };

  const goNext = () => {
    if (!articles.length) return;
    if (currentIndex < articles.length - 1) {
      setCurrentIndex((i) => i + 1);
    } else if (pagination.page < pagination.pages) {
      setPendingEdge('start');
      setPage(pagination.page + 1);
    }
  };

  const jumpTo = (idx) => {
    setCurrentIndex(idx);
    setShowQueueList(false);
  };

  const handleAiGenerate = async (type, extraData = null) => {
    if (type === 'slug' && !editForm.title) {
      return toast.error('Please enter a title first');
    }
    if (type === 'transliterate' && !extraData) {
      return toast.error('Please enter some text to convert');
    }

    setLoading(true);
    try {
      const payload = { type };
      if (type === 'slug') payload.title = editForm.title;
      if (type === 'transliterate') payload.text = extraData;

      const res = await api.post('/admin/ai/generate', payload);
      if (res.data.success) {
        if (type === 'slug') setEditForm(f => ({ ...f, slug: res.data.data }));
        if (type === 'transliterate') {
          return res.data.data;
        }
        toast.success('Generated');
      }
    } catch (err) {
      toast.error('AI operation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTransliterateEdit = async (field) => {
    const text = editForm[field];
    if (!text) return toast.error(`Please type something in ${field} first`);
    const result = await handleAiGenerate('transliterate', text);
    if (result) {
      const limit = field === 'title' ? 75 : field === 'summary' ? 400 : result.length;
      setEditForm(f => ({ ...f, [field]: result.slice(0, limit) }));
    }
  };

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/articles/${deleteModal.id}`);
      toast.success('Article rejected and deleted');
      setDeleteModal(null);
      fetchArticles();
    } catch (err) {
      toast.error('Reject failed');
    } finally {
      setDeleting(false);
    }
  };

  const confirmSchedule = async () => {
    if (!scheduleModal || !scheduleDate) return toast.error('Please select a date and time');
    setScheduling(true);
    try {
      const utcDate = new Date(scheduleDate).toISOString();
      await api.put(`/admin/articles/${scheduleModal.id}`, { status: 'scheduled', published_at: utcDate });
      toast.success('Article Scheduled');
      setScheduleModal(null);
      fetchArticles();
    } catch (err) {
      toast.error('Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const toggleViewFlag = async (field) => {
    if (!current) return;
    const nextValue = !current[field];
    try {
      await api.put(`/admin/articles/${current.id}`, { [field]: nextValue });
      setArticles(list => list.map(a => a.id === current.id ? { ...a, [field]: nextValue } : a));
      toast.success('Updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const [editUploading, setEditUploading] = useState(false);
  const [showEditWebPreview, setShowEditWebPreview] = useState(false);
  const [editCropper, setEditCropper] = useState({ active: false, image: null, type: null });

  const [editState, setEditState] = useState('');
  const [editCities, setEditCities] = useState([]);

  const openEdit = (article) => {
    setEditModal(article);
    const cityObj = cities.find(c => c.name === article.city);
    const rawState = cityObj ? cityObj.state : (article.state || '');
    const initialState = isAllIndiaState(rawState) ? ALL_INDIA_STATE + ' ' : (isInternationalState(rawState) ? INTERNATIONAL_STATE : rawState);
    setEditState(initialState);
    setEditCities(cities.filter(c => c.state && statesMatch(c.state, initialState)));

    setEditForm({
      title: article.title || '',
      summary: article.summary || '',
      category: article.category || '',
      city: article.city || '',
      source_url: article.source_url || '',
      source: article.source || '',
      media_type: article.media_type || 'image',
      image_url: article.image_url || '',
      thumbnail_url: article.thumbnail_url || '',
      video_url: article.video_url || '',
      is_breaking: article.is_breaking || false,
      breaking_state: article.breaking_state || ALL_INDIA_STATE,
      custom_notification_title: article.custom_notification_title || '',
      is_featured: article.is_featured || false,
      zone_only: article.zone_only || false,
      slug: (article.slug || '').split('-').slice(0, -1).join('-'),
      author_name: article.author_name || '',
      images: article.images ? article.images.map(img => img.image_url) : [],
      status: article.status || 'draft',
      published_at: article.published_at || '',
      manual_thumbnail: false,
    });
  };

  const handleEditFileSelect = (fileOrType, type) => {
    if (typeof fileOrType === 'string') {
      const currentType = fileOrType;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = currentType === 'video' ? 'video/mp4' : 'image/*';
      input.onchange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const selectedFile = files[0];
        if (currentType === 'video') {
          uploadEditToServer(selectedFile, currentType);
        } else {
          const reader = new FileReader();
          reader.onload = () => {
            setEditCropper({ active: true, image: reader.result, type: currentType });
          };
          reader.readAsDataURL(selectedFile);
        }
      };
      input.click();
      return;
    }

    const file = fileOrType;
    if (type === 'video') {
      uploadEditToServer(file, type);
    } else {
      if (file && file instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          setEditCropper({ active: true, image: reader.result, type });
        };
        reader.readAsDataURL(file);
      }
    }
  };

  useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (!editModal) return;
      if (editForm?.media_type !== 'image') return;
      if (editCropper.active || showEditWebPreview) return;

      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type && it.type.startsWith('image/')) {
          const file = it.getAsFile();
          if (file) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = () => {
              setEditCropper({ active: true, image: reader.result, type: 'image' });
            };
            reader.readAsDataURL(file);
            return;
          }
        }
      }
    };

    window.addEventListener('paste', handleGlobalPaste);
    return () => window.removeEventListener('paste', handleGlobalPaste);
  }, [editModal, editForm?.media_type, editCropper.active, showEditWebPreview]);

  const handleEditPasteImage = (e) => {
    e.preventDefault();
  };

  const handleEditCropComplete = async (blob) => {
    const type = editCropper.type;
    setEditCropper({ active: false, image: null, type: null });

    const file = new File([blob], `cropped-${Date.now()}.jpg`, { type: 'image/jpeg' });
    uploadEditToServer(file, type);
  };

  const uploadEditToServer = async (file, type) => {
    const formData = new FormData();
    formData.append('file', file);
    const endpoint = (type === 'image' || type === 'extra_image') ? '/admin/upload/image'
      : type === 'thumbnail' ? '/admin/upload/thumbnail'
        : '/admin/upload/video';

    setEditUploading(true);
    try {
      const res = await api.post(endpoint, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.data.url;
      const thumbnailUrl = res.data.data.thumbnail_url;

      if (type === 'image') setEditForm(f => ({ ...f, image_url: url }));
      else if (type === 'extra_image') setEditForm(f => ({ ...f, images: [...(f.images || []), url] }));
      else if (type === 'thumbnail') {
        setEditForm(f => ({ ...f, thumbnail_url: url, manual_thumbnail: true }));
      }
      else if (type === 'video') {
        setEditForm(f => {
          const nextForm = { ...f, video_url: url };
          if (thumbnailUrl && !f.manual_thumbnail) {
            nextForm.thumbnail_url = thumbnailUrl;
          }
          return nextForm;
        });
      }
      toast.success(`${type === 'extra_image' ? 'Image' : type} uploaded and resized!`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setEditUploading(false);
    }
  };

  const saveEdit = async () => {
    if (!editModal) return;
    try {
      await api.put(`/admin/articles/${editModal.id}`, editForm);
      toast.success('Article updated');
      setEditModal(null);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleEditSubmit = (e) => {
    if (e) e.preventDefault();
    saveEdit();
  };

  return (
    <div className="page">
      {/* Filters, search, and queue position/nav all in one compact row */}
      <div className="review-toolbar">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-input-wrap">
            <MdSearch className="search-icon" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles by title..."
              className="search-input"
            />
          </div>
          <select value={filters.category} onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}>
            <option value="">All Categories</option>
            {categories.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
          <select value={filters.city} onChange={(e) => setFilters(f => ({ ...f, city: e.target.value }))}>
            <option value="">All Cities</option>
            {cities.map(c => <option key={c.slug} value={c.name}>{c.name}</option>)}
          </select>
          <select value={filters.admin_id} onChange={(e) => setFilters(f => ({ ...f, admin_id: e.target.value }))}>
            <option value="">All Users</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        {!loading && articles.length > 0 && (
          <div className="review-toolbar-right">
            <span className="queue-position">
              <strong>{(pagination.page - 1) * limit + currentIndex + 1}</strong> of <strong>{pagination.total}</strong> draft{pagination.total === 1 ? '' : 's'}
            </span>
            <button className="btn btn-sm btn-ghost" onClick={goPrev} disabled={isFirstOverall}>‹ Previous</button>
            <button className="btn btn-sm btn-ghost" onClick={goNext} disabled={isLastOverall}>Next ›</button>
            <button className="btn btn-sm btn-ghost" onClick={() => setShowQueueList((s) => !s)}>
              {showQueueList ? 'Hide list' : `Show list (${pagination.total})`}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="page-loader">Loading...</div>
      ) : articles.length === 0 ? (
        <div className="empty-state">
          <MdInbox className="empty-icon" />
          <h3>Queue empty</h3>
          <p>No drafts pending review.</p>
        </div>
      ) : (
        <>
          {showQueueList && (
            <div className="queue-list">
              {articles.map((a, idx) => (
                <div key={a.id} className={`queue-list-item ${idx === currentIndex ? 'active' : ''}`} onClick={() => jumpTo(idx)}>
                  <span className="queue-list-title">{a.title || 'Untitled'}</span>
                  {a.is_breaking && <span className="badge badge-danger">Breaking</span>}
                </div>
              ))}
              <Pagination pagination={pagination} onPageChange={setPage} limit={limit} onLimitChange={setLimit} />
            </div>
          )}

          {/* Full single-item review card */}
          {current && (
            <div className="dashboard-card">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={e => setTitleDraft(e.target.value.slice(0, 75))}
                    onBlur={() => saveInlineField('title', titleDraft)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="Untitled"
                    className="inline-edit-field"
                    style={{ margin: 0, width: '100%', display: 'block', fontFamily: 'Ramabhadra, serif', fontSize: '28px', fontWeight: '400', color: 'var(--text)' }}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2, paddingLeft: 6 }}>
                    {savingField === 'title' ? 'Saving…' : `${titleDraft.length}/75`}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {current.is_breaking && <span className="breaking-tag selected" style={{ fontSize: '11px', display: 'inline-block', padding: '2px 6px' }}>Breaking</span>}
                    <span className="badge badge-category">{current.category?.replace('_', ' ')}</span>
                    <span className="badge badge-category">Draft</span>
                    <span className="badge">{current.city?.replace('_', ' ')}</span>
                    <span className="cr-meta" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {current.creator ? `${current.creator.name} (${current.creator.role})` : 'Unknown'}
                    </span>
                    <span className="cr-meta" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{formatDate(current.published_at)}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost"
                    style={{ color: 'var(--primary)', border: '1px solid var(--primary)', padding: '6px 12px', flexShrink: 0 }}
                    onClick={() => setShowWebPreview(true)}
                  >
                    <MdVisibility /> Website Preview
                  </button>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: (current.media_type === 'video' && current.video_url) || current.image_url ? '340px 1fr' : '1fr',
                gap: 24,
                alignItems: 'start',
              }}>
                {(current.media_type === 'video' && current.video_url) ? (
                  <video
                    src={current.video_url}
                    poster={current.thumbnail_url}
                    controls
                    className="detail-img"
                    style={{ objectFit: 'contain', background: '#000', maxHeight: '320px', width: '100%' }}
                  />
                ) : current.image_url ? (
                  <img src={current.image_url} alt="" className="detail-img" style={{ maxHeight: '320px', width: '100%', objectFit: 'cover' }} />
                ) : null}

                <div className="detail-row">
                  <span className="detail-label">
                    Summary {savingField === 'summary' && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(Saving…)</span>}
                  </span>
                  <textarea
                    ref={summaryRef}
                    value={summaryDraft}
                    onChange={e => setSummaryDraft(e.target.value.slice(0, 400))}
                    onBlur={() => saveInlineField('summary', summaryDraft)}
                    maxLength={400}
                    rows={4}
                    className="inline-edit-field"
                    style={{ whiteSpace: 'pre-wrap', fontSize: '18px', width: '100%', display: 'block', resize: 'none', overflow: 'hidden', fontFamily: 'inherit', minHeight: '100px' }}
                  />
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: 2 }}>{summaryDraft.length}/400</div>

                  <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginTop: 12 }}>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!current.is_breaking} onChange={() => toggleViewFlag('is_breaking')} />
                      <span className="breaking-tag">Breaking News</span>
                    </label>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!current.is_featured} onChange={() => toggleViewFlag('is_featured')} />
                      <span className="featured-tag">Featured Article</span>
                    </label>
                    <label className="checkbox-label" style={{ margin: 0 }}>
                      <input type="checkbox" checked={!!current.zone_only} onChange={() => toggleViewFlag('zone_only')} />
                      <span className="zone-tag" style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Show only in zone</span>
                    </label>
                  </div>

                  {current.is_breaking && (
                    <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '13px' }}>
                      <div style={{ color: '#991B1B', fontWeight: '600' }}>
                        Target State: <span style={{ color: 'var(--text)', fontWeight: '500' }}>{(current.breaking_state === 'zone_only' || (current.is_breaking && current.zone_only)) ? 'Show Only in Zone' : (current.breaking_state === ALL_INDIA_STATE || !current.breaking_state) ? 'All (Telangana + Andhra Pradesh)' : current.breaking_state}</span>
                      </div>
                      {current.custom_notification_title && (
                        <div style={{ color: '#991B1B', fontWeight: '600', marginTop: '4px' }}>
                          నోటిఫికేషన్ టైటిల్: <span style={{ color: 'var(--text)', fontWeight: '400' }}>{current.custom_notification_title}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {current.author_name && (
                <div className="detail-row" style={{ marginTop: 16 }}><span className="detail-label">Author</span><span>{current.author_name}</span></div>
              )}
              {current.source && (
                <div className="detail-row" style={{ marginTop: 16 }}><span className="detail-label">Source</span><span>{current.source}</span></div>
              )}
              {current.source_url && (
                <div className="detail-row" style={{ marginTop: 16 }}><span className="detail-label">Source URL</span><a href={current.source_url} target="_blank" rel="noreferrer" className="detail-link">{current.source_url}</a></div>
              )}
              {current.images && current.images.length > 0 && (
                <div className="detail-row" style={{ marginTop: 16 }}>
                  <span className="detail-label">Gallery</span>
                  <div className="flex-row gap-2 flex-wrap">
                    {current.images.map((img, i) => (
                      <img key={i} src={img.image_url} alt="" className="detail-img" style={{ width: 60, height: 60, objectFit: 'cover', margin: 0 }} />
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
                <button className="btn btn-ghost" onClick={() => openEdit(current)}>Edit</button>
                <button className="btn btn-danger" onClick={() => setDeleteModal(current)}><MdDelete /> Reject</button>
                <button
                  className="btn btn-primary"
                  style={
                    current.status !== 'draft'
                      ? { background: 'var(--bg)', color: 'var(--text-secondary)', borderColor: 'var(--border)', cursor: 'not-allowed', opacity: 0.7 }
                      : { background: '#3B82F6', borderColor: '#3B82F6', color: '#FFFFFF' }
                  }
                  disabled={current.status !== 'draft'}
                  onClick={() => setScheduleModal(current)}
                >
                  Schedule Publish
                </button>
                <button className="btn btn-primary" onClick={() => handlePreApprove(current)} disabled={checkingDuplicate || approving}>
                  {approving ? 'Publishing…' : <><MdCheckCircle /> Approve & Publish</>}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Article Modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Article</h3>
              <div className="flex-row items-center gap-4">
                <button
                  type="button"
                  className="btn btn-xs btn-ghost"
                  style={{ color: 'var(--primary)', border: '1px solid var(--primary)', padding: '6px 12px' }}
                  onClick={() => setShowEditWebPreview(true)}
                >
                  <MdVisibility /> Website Preview
                </button>
                <button className="modal-close" onClick={() => setEditModal(null)}><MdClose /></button>
              </div>
            </div>
            <div className="modal-body">
              <form className="modal-form" onSubmit={handleEditSubmit}>
                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">Title <span className="char-count">{(editForm.title || '').length}/75</span></label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleTransliterateEdit('title')} disabled={loading}>
                      ✨ Convert to Telugu
                    </button>
                  </div>
                  <input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value.slice(0, 75) }))} maxLength={75} required />
                </div>

                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">URL Slug (English)</label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={async () => {
                      setLoading(true);
                      try {
                        const res = await api.post('/admin/ai/generate', { type: 'slug', title: editForm.title });
                        if (res.data.success) setEditForm(f => ({ ...f, slug: res.data.data }));
                      } catch (err) { toast.error('Slug generation failed'); }
                      finally { setLoading(false); }
                    }} disabled={loading}>
                      ✨ AI Generate
                    </button>
                  </div>
                  <input value={editForm.slug} onChange={e => setEditForm(f => ({ ...f, slug: e.target.value }))} placeholder="e.g. latest-news" />
                </div>

                <div className="form-group">
                  <div className="flex-row justify-between items-center mb-1">
                    <label className="mb-0">Summary <span className="char-count">{(editForm.summary || '').length}/400</span></label>
                    <button type="button" className="btn btn-xs btn-ghost ai-btn" onClick={() => handleTransliterateEdit('summary')} disabled={loading}>
                      ✨ Convert to Telugu
                    </button>
                  </div>
                  <textarea value={editForm.summary} onChange={e => setEditForm(f => ({ ...f, summary: e.target.value.slice(0, 400) }))} rows={4} maxLength={400} required />
                </div>

                {/* Media */}
                <div className="form-group">
                  <label>Media Type</label>
                  <div className="radio-group">
                    <label className={`radio-card ${editForm.media_type === 'image' ? 'selected' : ''}`}>
                      <input type="radio" value="image" checked={editForm.media_type === 'image'}
                        onChange={() => setEditForm(f => ({ ...f, media_type: 'image' }))} /> Image
                    </label>
                    <label className={`radio-card ${editForm.media_type === 'video' ? 'selected' : ''}`}>
                      <input type="radio" value="video" checked={editForm.media_type === 'video'}
                        onChange={() => setEditForm(f => ({ ...f, media_type: 'video' }))} /> Video
                    </label>
                  </div>
                </div>

                {editForm.media_type === 'image' && (
                  <>
                    <div className="form-group">
                      <label>Article Image (Web & App)</label>
                      <div className="upload-area" tabIndex={0} onClick={() => handleEditFileSelect('image')} onPaste={handleEditPasteImage}>
                        {editForm.image_url ? (
                          <img src={editForm.image_url} alt="preview" className="upload-preview" />
                        ) : (
                          <p>{editUploading ? 'Processing...' : 'Click to select and crop image'}</p>
                        )}
                      </div>
                      <input type="text" value={editForm.image_url}
                        onChange={e => setEditForm(f => ({ ...f, image_url: e.target.value }))}
                        placeholder="Or paste image URL" className="mt-sm" />
                    </div>
                    <div className="form-group border-t border-gray-200 pt-4 mt-4">
                      <label>Additional Images (Gallery)</label>
                      <div className="flex-row gap-4 flex-wrap mb-sm">
                        {(editForm.images || []).map((imgUrl, i) => (
                          <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                            <img src={imgUrl} alt={`extra-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                            <button type="button" onClick={() => setEditForm(prev => ({ ...prev, images: prev.images.filter((_, index) => index !== i) }))} style={{ position: 'absolute', top: -5, right: -5, background: 'red', color: 'white', borderRadius: '50%', width: 20, height: 20, border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>X</button>
                          </div>
                        ))}
                        <div
                          onClick={() => handleEditFileSelect('extra_image')}
                          style={{ width: 80, height: 80, border: '2px dashed #ccc', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg)' }}
                        >
                          <span style={{ fontSize: 24, color: '#999' }}>+</span>
                        </div>
                      </div>
                      <p className="text-muted text-xs">Upload multiple images to create an image gallery for this article.</p>
                    </div>
                  </>
                )}

                {editForm.media_type === 'video' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', margin: 0 }}>Article Video</label>
                        {editForm.video_url && (
                          <span style={{ fontSize: '12px', color: '#16A34A', fontWeight: '500' }}>✓ Video Attached</span>
                        )}
                      </div>

                      {editForm.video_url ? (
                        <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--border)', backgroundColor: '#000' }}>
                          <video
                            src={editForm.video_url}
                            poster={editForm.thumbnail_url || undefined}
                            controls
                            style={{ width: '100%', maxHeight: '260px', display: 'block', objectFit: 'contain' }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'var(--bg)', borderTop: '1px solid #E5E7EB' }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleEditFileSelect('video')}
                              disabled={editUploading}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                            >
                              {editUploading ? 'Uploading video...' : '🔄 Replace Video'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => setEditForm(f => ({ ...f, video_url: '' }))}
                              style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
                            >
                              🗑️ Remove Video
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="upload-area"
                          onClick={() => handleEditFileSelect('video')}
                          style={{ minHeight: '140px', borderRadius: '10px', border: '2px dashed #CBD5E1', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--bg)', padding: '20px' }}
                        >
                          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 500 }}>
                            {editUploading ? 'Uploading video...' : '🎬 Click to upload video file (.mp4)'}
                          </p>
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>Supports MP4 video files</span>
                        </div>
                      )}
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px', display: 'block' }}>Or paste Video URL (MP4)</label>
                      <input
                        type="text"
                        value={editForm.video_url}
                        onChange={e => setEditForm(f => ({ ...f, video_url: e.target.value }))}
                        placeholder="https://..."
                        style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', width: '100%', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)', marginBottom: '6px', display: 'block' }}>Video Thumbnail (App & Web)</label>
                      <div
                        className="upload-area"
                        onClick={() => handleEditFileSelect('thumbnail')}
                        style={{ minHeight: '160px', borderRadius: '10px', border: '2px dashed #E5E7EB', cursor: 'pointer', backgroundColor: 'var(--bg)' }}
                      >
                        {editForm.thumbnail_url ? (
                          <img src={editForm.thumbnail_url} alt="thumbnail preview" className="upload-preview" />
                        ) : (
                          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
                            {editUploading ? 'Processing...' : 'Click to select and crop thumbnail'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="mb-0">Author Name</label>
                  <input type="text" value={editForm.author_name} onChange={e => setEditForm(f => ({ ...f, author_name: e.target.value }))} placeholder="Leave blank to show your real name" />
                </div>
                <div className="form-row">
                  <SearchableSelect
                    label="Category"
                    value={editForm.category}
                    options={categories}
                    onChange={(val) => setEditForm(f => ({ ...f, category: val }))}
                  />
                  <div className="form-group">
                    <label>Sub Category</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', minHeight: '42px' }}>
                      {['ఆంధ్రప్రదేశ్', 'తెలంగాణ', ALL_INDIA_STATE + ' ', INTERNATIONAL_STATE].map(stateName => {
                        const isSelected = editState && statesMatch(editState, stateName);
                        return (
                          <label key={stateName} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: isSelected ? '#EF233C' : '#333', fontWeight: isSelected ? '500' : '400' }}>
                            <input
                              type="radio"
                              name="edit_quick_state"
                              checked={isSelected}
                              onChange={() => {
                                setEditState(stateName);
                                const filtered = cities.filter(c => c.state && statesMatch(c.state, stateName));
                                setEditCities(filtered);
                                if (filtered.length > 0) setEditForm(f => ({ ...f, city: filtered[0].name }));
                              }}
                              style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#EF233C' }}
                            />
                            {stateName.trim()}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <SearchableSelect
                    label="City"
                    value={editForm.city}
                    options={editCities}
                    onChange={(val) => setEditForm(f => ({ ...f, city: val }))}
                  />
                </div>
                <div className="form-group">
                  <label>Source</label>
                  <input type="text" value={editForm.source} onChange={e => setEditForm(f => ({ ...f, source: e.target.value }))} placeholder="e.g. Times of India..." />
                </div>
                <div className="form-group">
                  <label>Source URL</label>
                  <input type="url" value={editForm.source_url} onChange={e => setEditForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={editForm.is_breaking}
                      onChange={(e) => setEditForm(f => ({ ...f, is_breaking: e.target.checked }))} />
                    <span className="breaking-tag">Breaking News</span>
                  </label>
                </div>
                {editForm.is_breaking && (
                  <div style={{ margin: '-6px 0 16px 0', padding: '16px 20px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px' }}>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', color: '#991B1B', marginBottom: '10px' }}>
                        Show Breaking News For
                      </label>
                      <div className="pill-radio-row">
                        {[
                          { key: 'తెలంగాణ', label: 'తెలంగాణ' },
                          { key: 'ఆంధ్రప్రదేశ్', label: 'ఆంధ్రప్రదేశ్' },
                          { key: ALL_INDIA_STATE, label: 'All (Telangana + Andhra Pradesh)' },
                          { key: 'zone_only', label: 'Show Only in Zone' }
                        ].map((option) => {
                          const isSelected = (editForm.breaking_state || ALL_INDIA_STATE).trim() === option.key.trim();
                          return (
                            <label key={option.key} className={`pill-radio${isSelected ? ' selected' : ''}`} style={{ fontSize: '15px', padding: '10px 18px' }}>
                              <input
                                type="radio"
                                name="edit_breaking_state"
                                value={option.key}
                                checked={isSelected}
                                onChange={() => setEditForm(f => ({
                                  ...f,
                                  breaking_state: option.key,
                                  ...(option.key === 'zone_only' ? { zone_only: true } : {})
                                }))}
                              />
                              {option.label}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '600', color: '#991B1B' }}>
                          Custom Notification Title (Optional)
                        </label>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          Leave blank to use original article title
                        </span>
                      </div>
                      <input
                        type="text"
                        value={editForm.custom_notification_title || ''}
                        onChange={(e) => setEditForm(f => ({ ...f, custom_notification_title: e.target.value }))}
                        placeholder="Example: Heavy Rain Alert in Hyderabad"
                        style={{ fontSize: '14px', padding: '10px 14px', borderColor: '#FCA5A5', width: '100%', borderRadius: '8px', border: '1.5px solid #FCA5A5', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                )}
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={editForm.is_featured}
                      onChange={(e) => setEditForm(f => ({ ...f, is_featured: e.target.checked }))} />
                    <span className="featured-tag">Featured Article</span> — show in hero banner
                  </label>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={editForm.zone_only}
                      onChange={(e) => setEditForm(f => ({ ...f, zone_only: e.target.checked }))} />
                    <span className="zone-tag" style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>Show only in zone</span> — excluded from the main feed
                  </label>
                </div>

                <div className="form-group">
                  <label>Article Status</label>
                  <select
                    value={editForm.status}
                    onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                {editForm.status === 'scheduled' && (
                  <div className="form-group">
                    <label>Schedule Date & Time</label>
                    <input
                      type="datetime-local"
                      value={editForm.published_at ? (() => {
                        const d = new Date(editForm.published_at);
                        if (isNaN(d.getTime())) return '';
                        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
                        return d.toISOString().slice(0, 16);
                      })() : ''}
                      onChange={e => {
                        if (e.target.value) {
                          setEditForm(f => ({ ...f, published_at: new Date(e.target.value).toISOString() }));
                        } else {
                          setEditForm(f => ({ ...f, published_at: '' }));
                        }
                      }}
                    />
                  </div>
                )}

              </form>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}



      {/* Schedule Confirmation Modal */}
      {scheduleModal && (
        <div className="modal-overlay" onClick={() => setScheduleModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Schedule Article</h3>
              <button className="modal-close" onClick={() => setScheduleModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Select date and time to publish this article automatically.</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{scheduleModal.title}"</p>
              <div className="form-group" style={{ marginTop: 15 }}>
                <label>Date & Time</label>
                <input
                  type="datetime-local"
                  value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setScheduleModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmSchedule} disabled={scheduling}>
                {scheduling ? 'Scheduling...' : 'Schedule Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Article</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to reject and delete this draft?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>"{deleteModal.title}"</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deleting}>
                {deleting ? 'Rejecting...' : 'Reject & Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="modal-overlay" onClick={() => setDuplicateWarning(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{ background: '#FEF2F2', padding: '20px 24px', borderBottom: '1px solid #FEE2E2', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
                ⚠️
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#991B1B' }}>Duplicate Article Found</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#B91C1C', marginTop: 2 }}>
                  This article is very similar to an existing published article.
                </p>
              </div>
              <button className="modal-close" onClick={() => setDuplicateWarning(null)}>
                <MdClose />
              </button>
            </div>

            <div className="modal-body" style={{ padding: 24, maxHeight: '55vh', overflowY: 'auto' }}>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px 0' }}>
                {duplicateWarning.duplicates.length} similar article{duplicateWarning.duplicates.length > 1 ? 's' : ''} found — review before publishing.
              </p>

              {duplicateWarning.duplicates.map((dup) => (
                <div key={dup.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {dup.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800, color: '#E11D48', padding: '2px 10px', background: '#FFE4E6', borderRadius: 20 }}>
                      {dup.ai_reason ? `${Math.round(dup.ai_confidence * 100)}%` : `${dup.score}%`}
                    </span>
                  </div>

                  <div className="dup-title" style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 6, lineHeight: 1.4 }}>
                    {dup.title}
                  </div>

                  {dup.ai_reason && (
                    <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                      {dup.ai_reason}
                    </p>
                  )}

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
                    <div><strong>Reporter:</strong> {dup.reporter_name || 'Admin'}</div>
                    <div><strong>Category:</strong> {dup.category}</div>
                    <div><strong>Published Date:</strong> {new Date(dup.published_at).toLocaleDateString()}</div>
                  </div>

                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ borderColor: '#0284C7', color: '#0284C7', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => setViewMatchedArticle(dup)}
                  >
                    <MdVisibility size={14} /> View Article
                  </button>
                </div>
              ))}

              {duplicateWarning.method === 'lexical_fallback' && (
                <p style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>
                  AI duplicate check was unavailable — showing keyword-similarity matches instead.
                </p>
              )}
            </div>

            <div className="modal-footer" style={{ padding: '16px 24px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                className="btn btn-danger-solid"
                style={{ background: '#EF233C', color: '#ffffff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                onClick={async () => {
                  const target = duplicateWarning.article;
                  setDeleting(true);
                  try {
                    await api.delete(`/admin/articles/${target.id}`);
                    toast.success('Article rejected successfully.');
                    setDuplicateWarning(null);
                    fetchArticles();
                  } catch (err) {
                    toast.error('Reject failed');
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
              >
                {deleting ? 'Rejecting...' : 'Reject Article'}
              </button>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  const target = duplicateWarning.article;
                  setApproving(true);
                  try {
                    await api.put(`/admin/articles/${target.id}`, { status: 'published' });
                    toast.success('Article Approved & Published');
                    setDuplicateWarning(null);
                    fetchArticles();
                  } catch (err) {
                    toast.error('Approve failed');
                  } finally {
                    setApproving(false);
                  }
                }}
                disabled={approving}
              >
                {approving ? 'Publishing...' : 'Publish Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matched Duplicate Article Single Preview Modal */}
      {viewMatchedArticle && (
        <div className="modal-overlay" onClick={() => setViewMatchedArticle(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, borderRadius: 16, overflow: 'hidden' }}>
            <div className="modal-header" style={{ background: 'var(--bg)', padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>
                Matched Duplicate Article Details (ID: #{viewMatchedArticle.id})
              </h3>
              <button className="modal-close" onClick={() => setViewMatchedArticle(null)}><MdClose /></button>
            </div>

            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#FFF1F2', border: '1px solid #FECDD3', padding: '10px 14px', borderRadius: 10, marginBottom: viewMatchedArticle.ai_reason ? 10 : 16 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#881337' }}>
                  {viewMatchedArticle.ai_reason ? 'AI Confidence' : 'Similarity Score'}
                </span>
                <span style={{ fontSize: 18, fontWeight: 800, color: '#E11D48' }}>
                  {viewMatchedArticle.ai_reason ? `${Math.round(viewMatchedArticle.ai_confidence * 100)}%` : `${viewMatchedArticle.score}%`} Match
                </span>
              </div>

              {viewMatchedArticle.ai_reason && (
                <p style={{ fontSize: 13, color: '#78350F', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '10px 12px', marginBottom: 16 }}>
                  {viewMatchedArticle.ai_reason}
                </p>
              )}

              {(viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url) && (
                <img
                  src={viewMatchedArticle.image_url || viewMatchedArticle.thumbnail_url}
                  alt=""
                  style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }}
                />
              )}

              <h4 style={{ margin: '0 0 10px 0', fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.4 }}>
                {viewMatchedArticle.title}
              </h4>

              {viewMatchedArticle.summary && (
                <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.5, marginBottom: 16, background: 'var(--bg)', padding: 12, borderRadius: 8, border: '1px solid var(--border)' }}>
                  {viewMatchedArticle.summary}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 12.5, color: 'var(--text-secondary)', background: 'var(--bg)', padding: 12, borderRadius: 10 }}>
                <div><strong>Article ID:</strong> #{viewMatchedArticle.id}</div>
                <div><strong>Reporter:</strong> {viewMatchedArticle.reporter_name || 'Admin'}</div>
                <div><strong>Category:</strong> {viewMatchedArticle.category || 'General'}</div>
                <div><strong>Published Date:</strong> {new Date(viewMatchedArticle.published_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div className="modal-footer" style={{ padding: '14px 20px', background: 'var(--bg)', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {viewMatchedArticle.slug ? (
                <a
                  href={`http://localhost:3000/${viewMatchedArticle.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-ghost"
                  style={{ borderColor: '#0284C7', color: '#0284C7', fontSize: 13 }}
                >
                  Open Web Article ↗
                </a>
              ) : <div />}
              <button className="btn btn-primary" onClick={() => setViewMatchedArticle(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditWebPreview && (
        <WebPreviewModal
          article={editForm}
          onClose={() => setShowEditWebPreview(false)}
        />
      )}

      {showWebPreview && current && (
        <WebPreviewModal
          article={current}
          onClose={() => setShowWebPreview(false)}
        />
      )}

      {editCropper.active && (
        <ImageCropper
          image={editCropper.image}
          onCropComplete={handleEditCropComplete}
          onCancel={() => setEditCropper({ active: false, image: null, type: null })}
        />
      )}
    </div>
  );
}
